import type {
  DateDate,
  DateTime,
  ExternalLink,
  InternalLink,
  Price,
} from '@backmarket/http-api'

import type { OrderlineBundledMobilePlan } from './OrderlineBundledMobilePlan.types'
import type { ExpressReplacedByReplacementOrderline } from './OrderlineExpressReplacement.types'
import type { OrderlineTimelineStep } from './OrderlineTimeline.types'

export type ActionType =
  | 'cancel_order_before_validation'
  | 'cancel_order_pending_payment'
  | 'diagnosis_new_issue'
  | 'diagnosis'
  | 'join_conversation'
  | 'live_chat'
  | 'open_conversation'
  | 'order_bill'
  | 'pending_order_bill'
  | 'sale_certificate'
  | 'see_return_confirmation'
  | 'see_express_replacement_confirmation'
  | 'track_parcel'
  | 'select_your_orderline'

export const ORDERLINE_ACTION: Record<string, ActionType> = {
  cancelOrderBeforeValidation: 'cancel_order_before_validation',
  cancelOrderPendingPayment: 'cancel_order_pending_payment',
  diagnosisNewIssue: 'diagnosis_new_issue',
  diagnosis: 'diagnosis',
  joinConversation: 'join_conversation',
  liveChat: 'live_chat',
  openConversation: 'open_conversation',
  orderBill: 'order_bill',
  pendingOrderBill: 'pending_order_bill',
  saleCertificate: 'sale_certificate',
  seeReturnConfirmation: 'see_return_confirmation',
  seeExpressReplacementConfirmation: 'see_express_replacement_confirmation',
  trackParcel: 'track_parcel',
  selectYourOrderline: 'select_your_orderline',
}

export type OrderlineState = {
  enumState:
    | 'PENDING_PAYMENT'
    | 'PAID'
    | 'VALIDATED'
    | 'SHIPPED'
    | 'DELIVERED'
    | 'CANCELLED'
    | 'REFUNDED'
    | 'NOT_PAID'
    | 'AWAITING_CONFIRMATION'
    | 'REPLACED'
    | 'EXPRESS_REPLACE_ONGOING'
    | 'EXPRESS_REPLACE_COMPLETE'
    | 'UNKNOWN'
  date: string | null
  label: string | null
  labelClass: 'DEFAULT' | 'SUCCESS' | 'DANGER' | 'WARNING' | null
}

export type OrderlineLink = {
  id: number
  /**
   * Publicid
   * @format uuid
   */
  publicId: string
  orderId: number
}

export type Tag = {
  label: string
  labelClass: 'DEFAULT' | 'INFO' | 'SUCCESS' | 'DANGER' | 'WARNING' | null
}

export type Orderline = {
  orderlineId: number
  /**
   * Publicid
   * @format uuid
   */
  publicId: string
  state: OrderlineState
  orderId: number
  creationDate: DateTime
  canBeRefundedAutomatically: boolean
  refundDate: DateTime | null
  product: {
    title: string
    image: string
    link: InternalLink
  }
  price: Price | null
  timeline: OrderlineTimelineStep[] | null
  actions: {
    type: ActionType
    active: boolean
    label: string
    hint: string | null
    link: InternalLink | ExternalLink | null
    downloadable: boolean
  }[]
  bundledMobilePlan: OrderlineBundledMobilePlan | null
  replacedByReplacementOrderline: OrderlineLink | null
  expressReplacedByReplacementOrderline: ExpressReplacedByReplacementOrderline | null
  expressReplacementReturnWindow: DateDate | null
  tag: Tag | null
}

export type OrderlineListResponse = {
  count: number
  next: string | null
  previous: string | null
  results: Orderline[]
}
